import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Bug, Lightbulb, Clock, CheckCircle, XCircle, PlayCircle, Filter, Send, ChevronDown, ChevronUp, Download, X, ZoomIn } from 'lucide-react';
import api from '../../services/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const fullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
};

const downloadImage = async (url, filename) => {
  try {
    const res = await fetch(fullUrl(url));
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename || 'screenshot.jpg';
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(fullUrl(url), '_blank');
  }
};

const STATUS_CONFIG = {
  pending:     { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', Icon: Clock },
  approved:    { label: 'Approved',       color: 'bg-blue-100 text-blue-700',   Icon: CheckCircle },
  rejected:    { label: 'Rejected',       color: 'bg-red-100 text-red-700',     Icon: XCircle },
  in_progress: { label: 'In Progress',    color: 'bg-purple-100 text-purple-700', Icon: PlayCircle },
  done:        { label: 'Done',           color: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>
      <cfg.Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function TicketCard({ ticket, onStatusChange, onResolution }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resolution, setResolution] = useState('');
  const [showResolutionInput, setShowResolutionInput] = useState(false);
  const [note, setNote] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(ticket.copy_text || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleStatusUpdate = async (newStatus) => {
    await onStatusChange(ticket.ticket_id, newStatus, note);
    setNote('');
  };

  const handlePostResolution = async () => {
    if (!resolution.trim()) return;
    await onResolution(ticket.ticket_id, resolution);
    setShowResolutionInput(false);
    setResolution('');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
      {/* Card Header */}
      <div className="p-4 flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${ticket.type === 'bug' ? 'bg-red-100' : 'bg-amber-100'}`}>
          {ticket.type === 'bug' ? <Bug className="w-4 h-4 text-red-600" /> : <Lightbulb className="w-4 h-4 text-amber-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-gray-800">{ticket.ticket_id}</span>
            <StatusBadge status={ticket.status} />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{ticket.submitted_by_name} ({ticket.submitted_by_role}) · {ticket.page_context}</p>
          <p className="text-xs text-gray-400 mt-0.5">{new Date(ticket.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</p>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Raw description preview — full text, no truncation */}
      {!expanded && (
        <div className="px-4 pb-3">
          <p className="text-sm text-gray-600">{ticket.raw_description}</p>
        </div>
      )}

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {/* AI Summary */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">AI Summary (Copy to Emergent)</p>
            <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-mono">{ticket.ai_summary}</pre>
          </div>

          {/* Screenshots */}
          {ticket.screenshot_urls?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Screenshots</p>
              <div className="space-y-3">
                {ticket.screenshot_urls.map((url, i) => (
                  <div key={i} className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                    <img
                      src={fullUrl(url)}
                      alt={`Screenshot ${i + 1}`}
                      className="w-full object-contain max-h-64 cursor-zoom-in"
                      onClick={() => setLightboxUrl(fullUrl(url))}
                    />
                    <div className="absolute top-2 right-2 flex gap-1.5">
                      <button
                        onClick={() => setLightboxUrl(fullUrl(url))}
                        className="bg-black/50 hover:bg-black/70 text-white rounded-lg p-1.5 transition-colors"
                        title="View full size"
                        data-testid={`zoom-screenshot-${i}`}
                      >
                        <ZoomIn className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => downloadImage(url, `${ticket.ticket_id}-screenshot-${i + 1}.jpg`)}
                        className="bg-black/50 hover:bg-black/70 text-white rounded-lg p-1.5 transition-colors"
                        title="Save photo"
                        data-testid={`download-screenshot-${i}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lightbox */}
          {lightboxUrl && (
            <div
              className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
              onClick={() => setLightboxUrl(null)}
              data-testid="screenshot-lightbox"
            >
              <div className="relative max-w-full max-h-full" onClick={e => e.stopPropagation()}>
                <img src={lightboxUrl} alt="Full size screenshot" className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl" />
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    onClick={() => downloadImage(lightboxUrl, `${ticket.ticket_id}-screenshot.jpg`)}
                    className="bg-white/20 hover:bg-white/40 text-white rounded-xl px-3 py-2 flex items-center gap-1.5 text-sm font-medium transition-colors backdrop-blur-sm"
                    data-testid="lightbox-download-btn"
                  >
                    <Download className="w-4 h-4" /> Save Photo
                  </button>
                  <button
                    onClick={() => setLightboxUrl(null)}
                    className="bg-white/20 hover:bg-white/40 text-white rounded-xl p-2 transition-colors backdrop-blur-sm"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Copy to Emergent */}
          <button
            onClick={handleCopy}
            data-testid={`copy-emergent-btn-${ticket.ticket_id}`}
            className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-[#075E54] text-[#075E54] rounded-xl font-semibold text-sm hover:bg-[#075E54] hover:text-white transition-colors"
          >
            {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy to Emergent</>}
          </button>

          {/* Admin Actions */}
          {ticket.status === 'pending' && (
            <div className="flex gap-2">
              <button onClick={() => handleStatusUpdate('approved')} data-testid={`approve-btn-${ticket.ticket_id}`} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Approve</button>
              <button onClick={() => handleStatusUpdate('rejected')} data-testid={`reject-btn-${ticket.ticket_id}`} className="flex-1 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50">Reject</button>
            </div>
          )}
          {ticket.status === 'approved' && (
            <button onClick={() => handleStatusUpdate('in_progress')} className="w-full py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">Mark In Progress</button>
          )}

          {/* Post Resolution */}
          {['approved', 'in_progress'].includes(ticket.status) && (
            <div>
              {!showResolutionInput ? (
                <button onClick={() => setShowResolutionInput(true)} data-testid={`post-resolution-btn-${ticket.ticket_id}`} className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" /> Post Resolution to User
                </button>
              ) : (
                <div className="space-y-2">
                  <textarea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    rows={3}
                    placeholder="Paste the agent's completion summary here..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    data-testid={`resolution-input-${ticket.ticket_id}`}
                  />
                  <div className="flex gap-2">
                    <button onClick={handlePostResolution} disabled={!resolution.trim()} className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">Send to User</button>
                    <button onClick={() => { setShowResolutionInput(false); setResolution(''); }} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Resolution display */}
          {ticket.resolution_summary && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-emerald-700 mb-1">Resolution Sent to User:</p>
              <p className="text-sm text-emerald-800">{ticket.resolution_summary}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FeedbackInbox() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  const fetchTickets = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;
      const res = await api.get('/feedback', { params });
      setTickets(res.data);
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Mark feedback as read when admin opens this page
  useEffect(() => { api.post('/feedback/mark-read').catch(() => {}); }, []);

  const handleStatusChange = async (ticketId, status, note) => {
    try {
      await api.patch(`/feedback/${ticketId}/status`, { status, admin_note: note });
      fetchTickets();
    } catch {}
  };

  const handleResolution = async (ticketId, resolution) => {
    try {
      await api.post(`/feedback/${ticketId}/resolution`, { resolution_summary: resolution });
      fetchTickets();
    } catch {}
  };

  const counts = tickets.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white/80 hover:text-white"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="font-bold text-lg">Feedback Inbox</h1>
          <p className="text-white/70 text-xs">{tickets.length} tickets · {counts.pending || 0} pending review</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="px-4 py-3 grid grid-cols-4 gap-2">
        {[['pending', 'Pending', 'bg-amber-50 text-amber-700'], ['approved', 'Approved', 'bg-blue-50 text-blue-700'], ['in_progress', 'In Progress', 'bg-purple-50 text-purple-700'], ['done', 'Done', 'bg-emerald-50 text-emerald-700']].map(([s, l, c]) => (
          <button key={s} onClick={() => setFilterStatus(filterStatus === s ? '' : s)} className={`rounded-xl p-2 text-center transition-all border-2 ${filterStatus === s ? 'border-gray-800' : 'border-transparent'} ${c}`}>
            <div className="text-lg font-bold">{counts[s] || 0}</div>
            <div className="text-[10px] font-medium">{l}</div>
          </button>
        ))}
      </div>

      {/* Type Filter */}
      <div className="px-4 pb-3 flex gap-2">
        {[['', 'All'], ['bug', 'Bugs'], ['feature', 'Features']].map(([v, l]) => (
          <button key={v} onClick={() => setFilterType(v)} className={`text-xs px-3 py-1.5 rounded-full font-medium border transition-colors ${filterType === v ? 'bg-[#075E54] text-white border-[#075E54]' : 'border-gray-200 text-gray-600 hover:border-[#075E54]'}`}>{l}</button>
        ))}
      </div>

      {/* Ticket List */}
      <div className="px-4 pb-8 space-y-3">
        {loading ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading tickets…</div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Filter className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No tickets found</p>
          </div>
        ) : (
          tickets.map((t) => (
            <TicketCard key={t.ticket_id} ticket={t} onStatusChange={handleStatusChange} onResolution={handleResolution} />
          ))
        )}
      </div>
    </div>
  );
}
